import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmyTime } from "@/lib/format";

export default function Returns() {
  const { can } = useAuth();
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!can("pharmacy.sales.view")) return;
    Promise.all([api.get("/pharmacy/sales-returns"), api.get("/pharmacy/purchase-returns")])
      .then(([s, p]) => { setSales(s.data.items); setPurchases(p.data.items); })
      .catch((e) => setError(apiError(e)));
  }, [can]);

  if (!can("pharmacy.sales.view")) return <NoAccess what="returns" />;
  if (error) return <ErrorState message={error} />;
  if (!sales) return <Loading />;

  return (
    <div data-testid="returns-page">
      <PageHeader overline="Pharmacy" title="Returns"
        subtitle="Credit notes for customer returns and debit notes for supplier returns, each with a stock movement." />
      <div className="space-y-10">
        <section>
          <p className="overline text-[#EA580C] mb-4">Sales returns (credit notes)</p>
          <Table testid="sales-returns-table" columns={["Credit note", "Invoice", "Customer", "Items", "Refund", "Reason", "By", "Date"]}
            rows={sales} empty={<EmptyState title="No sales returns" subtitle="Process a return from Sales History." testid="sales-returns-empty" />}
            renderRow={(r) => (
              <tr key={r.return_id} data-testid={`sales-return-row-${r.return_id}`}>
                <Td className="font-mono-num text-slate-900">{r.credit_note_no}</Td>
                <Td className="text-slate-600">{r.invoice_no}</Td>
                <Td className="text-slate-600">{r.customer_name}</Td>
                <Td className="text-slate-600">{r.items.length}</Td>
                <Td className="text-slate-900">{inr(r.refund)}</Td>
                <Td className="text-slate-600 max-w-[200px] truncate">{r.reason}</Td>
                <Td className="text-slate-600">{r.created_by_name || "—"}</Td>
                <Td className="text-slate-500 whitespace-nowrap">{dmyTime(r.created_at)}</Td>
              </tr>
            )} />
        </section>
        <section>
          <p className="overline text-[#EA580C] mb-4">Purchase returns (debit notes)</p>
          <Table testid="purchase-returns-table" columns={["Debit note", "Purchase", "Supplier", "Items", "Amount", "Reason", "By", "Date"]}
            rows={purchases} empty={<EmptyState title="No purchase returns" subtitle="Process a return from the Purchases page." testid="purchase-returns-empty" />}
            renderRow={(r) => (
              <tr key={r.return_id} data-testid={`purchase-return-row-${r.return_id}`}>
                <Td className="font-mono-num text-slate-900">{r.debit_note_no}</Td>
                <Td className="text-slate-600">{r.purchase_no}</Td>
                <Td className="text-slate-600">{r.supplier_name}</Td>
                <Td className="text-slate-600">{r.items.length}</Td>
                <Td className="text-slate-900">{inr(r.total)}</Td>
                <Td className="text-slate-600 max-w-[200px] truncate">{r.reason}</Td>
                <Td className="text-slate-600">{r.created_by_name || "—"}</Td>
                <Td className="text-slate-500 whitespace-nowrap">{dmyTime(r.created_at)}</Td>
              </tr>
            )} />
        </section>
      </div>
    </div>
  );
}
